/**
 * Onboarding API Client
 *
 * Provides methods for company onboarding: creation, NAICS classification,
 * data readiness assessment, quick-setup, and import templates.
 */

import axios, { AxiosInstance } from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8000'

export interface CompanyCreateRequest {
  name: string
  city?: string
  state?: string
  street_address?: string
  zip_code?: string
  web_url?: string
  linkedin_url?: string
  revenue_tier?: string
  industry_hint?: string
  employee_count?: number
  naics_code?: string
}

export interface CompanyCreateResponse {
  status: string
  company_id: number
  naics_code: string | null
  industry_label: string | null
  ivp_status: string
}

export interface NaicsSuggestion {
  naics_code: string
  industry_label: string
  specificity: string
  rank: number
}

export interface ClassifyIndustryResponse {
  status: string
  suggestions: NaicsSuggestion[]
}

export interface DataReadinessResponse {
  company_id: number
  tier: 'identity' | 'sparse' | 'partial' | 'complete' | 'rich'
  domain_counts: Record<string, number>
  vitals_count: number
  sales_months: number
  ivp_status: string
  populated_domains: number
}

export interface ImportTemplate {
  naics_code: string
  description: string
  tables: Record<string, {
    filename: string
    columns: string[]
  }>
}

class OnboardingApiClient {
  private client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: { 'Content-Type': 'application/json' },
      timeout: 30000,
    })

    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('jwt_token')
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    })

    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('jwt_token')
          window.location.href = 'http://127.0.0.1:5000'
        }
        return Promise.reject(error)
      }
    )
  }

  async createCompany(data: CompanyCreateRequest): Promise<CompanyCreateResponse> {
    const response = await this.client.post<CompanyCreateResponse>('/onboarding/company', data)
    return response.data
  }

  async classifyIndustry(description: string, city?: string, state?: string): Promise<ClassifyIndustryResponse> {
    const response = await this.client.post<ClassifyIndustryResponse>('/onboarding/classify-industry', {
      description, city, state,
    })
    return response.data
  }

  async getReadiness(companyId: number): Promise<DataReadinessResponse> {
    const response = await this.client.get<DataReadinessResponse>(`/onboarding/${companyId}/readiness`)
    return response.data
  }

  async quickSetup(companyId: number, values: Record<string, string>): Promise<{ status: string; message: string }> {
    const response = await this.client.post(`/onboarding/${companyId}/quick-setup`, { values })
    return response.data
  }

  async getImportTemplate(naicsCode: string): Promise<{ status: string; template: ImportTemplate }> {
    const response = await this.client.get(`/onboarding/import-templates/${naicsCode}`)
    return response.data
  }
}

export const onboardingApi = new OnboardingApiClient()
